import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Button,
    Avatar,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
    TablePagination,
    Alert,
    Checkbox,
    Menu,
    ListItemIcon,
    ListItemText,
    Divider,
    Badge,
    InputAdornment,
    Fade,
    Zoom,
    Grid,
    Switch,
    FormControlLabel
} from '@mui/material';
import {
    Edit,
    Delete,
    Block,
    CheckCircle,
    PersonAdd,
    VpnKey,
    AdminPanelSettings,
    Person,
    Search,
    MoreVert,
    Group,
    SupervisorAccount,
    Email,
    Verified,
    Schedule,
    StarBorder,
    Refresh,
    SecurityUpdateGood,
    PersonRemove,
    GroupAdd,
    ManageAccounts,
    Visibility,
} from '@mui/icons-material';
import {
    getUsersPaginated,
    changeUserStatus,
    changeUserRole,
    deleteUser,
    resetUserPassword,
    createAdmin,
    createUser,
    editUser,
    bulkChangeUserStatus,
    bulkDeleteUsers,
    getUserDetails
} from '../../../api/admin';
import { AuthContext } from '../../../contexts/AuthContext';
import UserDetailsDialog from './UserDetailsDialog';

const UserManagementTable = () => {
    const { user: currentUser } = useContext(AuthContext);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [filterRole, setFilterRole] = useState('ALL');
    const [filterStatus, setFilterStatus] = useState('ALL');

    // Dialog states
    const [editDialog, setEditDialog] = useState({ open: false, user: null, mode: 'edit' });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
    const [resetDialog, setResetDialog] = useState({ open: false, user: null, newPassword: '' });
    const [createDialog, setCreateDialog] = useState({ open: false, type: 'user' });
    const [bulkDialog, setBulkDialog] = useState({ open: false, action: null });
    const [userDetailsDialog, setUserDetailsDialog] = useState({ open: false, userId: null });

    // Menu states
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        email: '',
        displayName: '',
        password: '',
        role: 'USER',
        isActive: true,
        isVerified: false
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getUsersPaginated(page, rowsPerPage, 'createdAt,desc');

            let usersData = response.data.content || [];

            // Apply local filters
            if (searchTerm) {
                usersData = usersData.filter(user =>
                    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
                );
            }

            if (filterRole !== 'ALL') {
                usersData = usersData.filter(user => user.role === filterRole);
            }

            if (filterStatus !== 'ALL') {
                const isActive = filterStatus === 'ACTIVE';
                usersData = usersData.filter(user => user.isActive === isActive);
            }

            setUsers(usersData);
            setTotalUsers(response.data.totalElements || usersData.length);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            setError('Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [page, rowsPerPage]);

    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (page === 0) {
                fetchUsers();
            } else {
                setPage(0);
            }
        }, 500);

        return () => clearTimeout(delayedSearch);
    }, [searchTerm, filterRole, filterStatus]);

    const handleStatusChange = async (userId, newStatus) => {
        try {
            await changeUserStatus(userId, newStatus);
            setSuccess(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
            fetchUsers();
        } catch (error) {
            console.error('Failed to change user status:', error);
            setError('Failed to change user status');
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await changeUserRole(userId, newRole);
            setSuccess(`User role changed to ${newRole} successfully`);
            setEditDialog({ open: false, user: null, mode: 'edit' });
            fetchUsers();
        } catch (error) {
            console.error('Failed to change user role:', error);
            setError('Failed to change user role');
        }
    };

    const handleDeleteUser = async () => {
        try {
            await deleteUser(deleteDialog.user.id);
            setSuccess('User deleted successfully');
            setDeleteDialog({ open: false, user: null });
            fetchUsers();
        } catch (error) {
            console.error('Failed to delete user:', error);
            setError('Failed to delete user');
        }
    };

    const handleResetPassword = async () => {
        try {
            await resetUserPassword(resetDialog.user.id, resetDialog.newPassword);
            setSuccess('Password reset successfully');
            setResetDialog({ open: false, user: null, newPassword: '' });
        } catch (error) {
            console.error('Failed to reset password:', error);
            setError('Failed to reset password');
        }
    };

    const handleCreateUser = async () => {
        try {
            if (createDialog.type === 'admin') {
                await createAdmin(formData);
                setSuccess('Admin created successfully');
            } else {
                await createUser(formData);
                setSuccess('User created successfully');
            }
            setCreateDialog({ open: false, type: 'user' });
            setFormData({
                email: '',
                displayName: '',
                password: '',
                role: 'USER',
                isActive: true,
                isVerified: false
            });
            fetchUsers();
        } catch (error) {
            console.error('Failed to create user:', error);
            setError('Failed to create user');
        }
    };

    const handleBulkAction = async () => {
        try {
            if (bulkDialog.action === 'activate') {
                await bulkChangeUserStatus(selectedUsers, true);
                setSuccess(`${selectedUsers.length} users activated successfully`);
            } else if (bulkDialog.action === 'deactivate') {
                await bulkChangeUserStatus(selectedUsers, false);
                setSuccess(`${selectedUsers.length} users deactivated successfully`);
            } else if (bulkDialog.action === 'delete') {
                await bulkDeleteUsers(selectedUsers);
                setSuccess(`${selectedUsers.length} users deleted successfully`);
            }

            setBulkDialog({ open: false, action: null });
            setSelectedUsers([]);
            fetchUsers();
        } catch (error) {
            console.error('Failed to perform bulk action:', error);
            setError('Failed to perform bulk action');
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedUsers(users.map(user => user.id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const openMenu = (event, user) => {
        setMenuAnchor(event.currentTarget);
        setSelectedUser(user);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
        setSelectedUser(null);
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'SUPER_ADMIN': return '#d32f2f';
            case 'ADMIN': return '#f57c00';
            case 'USER': return '#388e3c';
            default: return '#757575';
        }
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case 'SUPER_ADMIN': return <SupervisorAccount />;
            case 'ADMIN': return <AdminPanelSettings />;
            case 'USER': return <Person />;
            default: return <Person />;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) {
            return 'Never';
        }
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
    const isAdmin = currentUser?.role === 'ADMIN' || isSuperAdmin;

    const handleViewUserDetails = (user) => {
        setUserDetailsDialog({ open: true, userId: user.id });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Fade in={loading} timeout={500}>
                    <Stack alignItems="center" spacing={3}>
                        <Zoom in={true} timeout={500}>
                            <Avatar sx={{
                                width: 80,
                                height: 80,
                                background: 'linear-gradient(135deg, #2196f3, #42a5f5)',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.1)' },
                                    '100%': { transform: 'scale(1)' }
                                }
                            }}>
                                <Group sx={{ fontSize: 40, color: 'white' }} />
                            </Avatar>
                        </Zoom>
                        <Typography variant="h6" fontWeight={600} color="text.secondary">
                            Loading User Management...
                        </Typography>
                    </Stack>
                </Fade>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Card elevation={6} sx={{ borderRadius: 4, overflow: 'hidden' }}>
                {/* Enhanced Header */}
                <Box sx={{
                    p: 4,
                    background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <Box sx={{
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                        animation: 'float 6s ease-in-out infinite',
                        '@keyframes float': {
                            '0%': { transform: 'translateY(0px)' },
                            '50%': { transform: 'translateY(-20px)' },
                            '100%': { transform: 'translateY(0px)' }
                        }
                    }} />

                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                                <Avatar sx={{
                                    background: 'rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(10px)',
                                    border: '3px solid rgba(255,255,255,0.3)',
                                    width: 70,
                                    height: 70
                                }}>
                                    <Group sx={{ fontSize: 35 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
                                        Advanced User Management
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                        Comprehensive user control and administration
                                    </Typography>
                                </Box>
                            </Box>

                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                <Chip
                                    icon={<Group />}
                                    label={`${totalUsers} Total Users`}
                                    sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                />
                                <Chip
                                    icon={<CheckCircle />}
                                    label={`${users.filter(u => u.isActive).length} Active`}
                                    sx={{ background: 'rgba(76, 175, 80, 0.8)', color: 'white', fontWeight: 600 }}
                                />
                                <Chip
                                    icon={<AdminPanelSettings />}
                                    label={`${users.filter(u => u.role === 'ADMIN' || u.role === 'SUPER_ADMIN').length} Admins`}
                                    sx={{ background: 'rgba(255, 152, 0, 0.8)', color: 'white', fontWeight: 600 }}
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Stack spacing={2}>
                                {isSuperAdmin && (
                                    <Button
                                        variant="contained"
                                        startIcon={<PersonAdd />}
                                        onClick={() => {
                                            setCreateDialog({ open: true, type: 'admin' });
                                            setFormData({ ...formData, role: 'ADMIN' });
                                        }}
                                        sx={{
                                            background: 'rgba(244, 67, 54, 0.9)',
                                            '&:hover': { background: 'rgba(244, 67, 54, 1)' },
                                            fontWeight: 600
                                        }}
                                        fullWidth
                                    >
                                        Create Admin
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    startIcon={<GroupAdd />}
                                    onClick={() => {
                                        setCreateDialog({ open: true, type: 'user' });
                                        setFormData({ ...formData, role: 'USER' });
                                    }}
                                    sx={{
                                        background: 'rgba(255,255,255,0.2)',
                                        '&:hover': { background: 'rgba(255,255,255,0.3)' },
                                        fontWeight: 600
                                    }}
                                    fullWidth
                                >
                                    Add User
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                <CardContent sx={{ p: 0 }}>
                    {/* Advanced Filters & Search */}
                    <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    placeholder="Search users by email or name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'white',
                                            borderRadius: 2
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Role Filter</InputLabel>
                                    <Select
                                        value={filterRole}
                                        label="Role Filter"
                                        onChange={(e) => setFilterRole(e.target.value)}
                                        sx={{ backgroundColor: 'white', borderRadius: 2 }}
                                    >
                                        <MenuItem value="ALL">All Roles</MenuItem>
                                        <MenuItem value="USER">Users</MenuItem>
                                        <MenuItem value="ADMIN">Admins</MenuItem>
                                        <MenuItem value="SUPER_ADMIN">Super Admins</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Status Filter</InputLabel>
                                    <Select
                                        value={filterStatus}
                                        label="Status Filter"
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        sx={{ backgroundColor: 'white', borderRadius: 2 }}
                                    >
                                        <MenuItem value="ALL">All Status</MenuItem>
                                        <MenuItem value="ACTIVE">Active</MenuItem>
                                        <MenuItem value="INACTIVE">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Refresh />}
                                    onClick={fetchUsers}
                                    fullWidth
                                    sx={{ py: 1.5, borderRadius: 2 }}
                                >
                                    Refresh
                                </Button>
                            </Grid>

                            <Grid item xs={12} md={2}>
                                {selectedUsers.length > 0 && (
                                    <Badge badgeContent={selectedUsers.length} color="primary">
                                        <Button
                                            variant="contained"
                                            startIcon={<ManageAccounts />}
                                            onClick={() => setBulkDialog({ open: true, action: 'manage' })}
                                            fullWidth
                                            sx={{ py: 1.5, borderRadius: 2 }}
                                        >
                                            Bulk Actions
                                        </Button>
                                    </Badge>
                                )}
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Advanced Table */}
                    <TableContainer>
                        <Table sx={{ minWidth: 1000 }}>
                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                                            checked={users.length > 0 && selectedUsers.length === users.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>User Profile</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Contact & Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Role & Permissions</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Activity & Points</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Account Info</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 8 }}>
                                            <Group sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                                            <Typography variant="h6" color="text.secondary">
                                                No users found
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {searchTerm || filterRole !== 'ALL' || filterStatus !== 'ALL'
                                                    ? 'Try adjusting your filters'
                                                    : 'Start by creating your first user'
                                                }
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((user) => (
                                        <TableRow
                                            key={user.id}
                                            hover
                                            selected={selectedUsers.includes(user.id)}
                                            sx={{
                                                '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                                                height: 80
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedUsers.includes(user.id)}
                                                    onChange={() => handleSelectUser(user.id)}
                                                />
                                            </TableCell>

                                            {/* User Profile */}
                                            <TableCell>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Badge
                                                        overlap="circular"
                                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                        badgeContent={
                                                            user.isActive ? (
                                                                <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />
                                                            ) : (
                                                                <Block sx={{ fontSize: 16, color: '#f44336' }} />
                                                            )
                                                        }
                                                    >
                                                        <Avatar
                                                            src={user.profilePhotoPath}
                                                            sx={{
                                                                width: 45,
                                                                height: 45,
                                                                border: `2px solid ${getRoleColor(user.role)}20`,
                                                                background: `linear-gradient(135deg, ${getRoleColor(user.role)}20, ${getRoleColor(user.role)}40)`
                                                            }}
                                                        >
                                                            {user.displayName?.charAt(0)?.toUpperCase()}
                                                        </Avatar>
                                                    </Badge>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                                                            {user.displayName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            ID: #{user.id}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>

                                            {/* Contact & Status */}
                                            <TableCell>
                                                <Stack spacing={1}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Email sx={{ fontSize: 14, color: '#666' }} />
                                                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                            {user.email}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        {user.isVerified && (
                                                            <Chip
                                                                icon={<Verified />}
                                                                label="Verified"
                                                                size="small"
                                                                color="success"
                                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                                            />
                                                        )}
                                                        <Chip
                                                            label={user.isActive ? 'Active' : 'Inactive'}
                                                            size="small"
                                                            color={user.isActive ? 'success' : 'error'}
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                                        />
                                                    </Stack>
                                                </Stack>
                                            </TableCell>

                                            {/* Role & Permissions */}
                                            <TableCell>
                                                <Chip
                                                    icon={getRoleIcon(user.role)}
                                                    label={user.role.replace('_', ' ')}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: `${getRoleColor(user.role)}15`,
                                                        color: getRoleColor(user.role),
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        border: `1px solid ${getRoleColor(user.role)}30`
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Activity & Points */}
                                            <TableCell>
                                                <Stack spacing={1}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <StarBorder sx={{ fontSize: 14, color: '#ff9800' }} />
                                                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                                                            {user.rewardsPoints || 0} pts
                                                        </Typography>
                                                    </Stack>
                                                    {user.lastLoginAt && (
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Schedule sx={{ fontSize: 12, color: '#666' }} />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formatDate(user.lastLoginAt)}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </TableCell>

                                            {/* Account Info */}
                                            <TableCell>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Created: {formatDate(user.createdAt)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Lang: {user.preferredLanguage || 'en'}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}>

                                                    <Tooltip title={user.isActive ? 'Deactivate' : 'Activate'}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleStatusChange(user.id, !user.isActive)}
                                                            sx={{
                                                                color: user.isActive ? '#ff9800' : '#4caf50',
                                                                '&:hover': {
                                                                    backgroundColor: user.isActive ? '#ff980020' : '#4caf5020'
                                                                }
                                                            }}
                                                        >
                                                            {user.isActive ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewUserDetails(user)}
                                                            sx={{
                                                                color: '#1976d2',
                                                                '&:hover': { backgroundColor: '#1976d220' }
                                                            }}
                                                        >
                                                            <Visibility fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="More Actions">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => openMenu(e, user)}
                                                            sx={{
                                                                color: '#666',
                                                                '&:hover': { backgroundColor: '#66666620' }
                                                            }}
                                                        >
                                                            <MoreVert fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Enhanced Pagination */}
                    {users.length > 0 && (
                        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                            <TablePagination
                                component="div"
                                count={totalUsers}
                                page={page}
                                onPageChange={(event, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(event) => {
                                    setRowsPerPage(parseInt(event.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Action Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                PaperProps={{
                    sx: { minWidth: 200, borderRadius: 2 }
                }}
            >
                <MenuItem onClick={() => {
                    setResetDialog({ open: true, user: selectedUser, newPassword: '' });
                    closeMenu();
                }}>
                    <ListItemIcon>
                        <VpnKey fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Reset Password</ListItemText>
                </MenuItem>

                <MenuItem onClick={() => {
                    setEditDialog({ open: true, user: selectedUser, mode: 'role' });
                    closeMenu();
                }}>
                    <ListItemIcon>
                        <SecurityUpdateGood fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Change Role</ListItemText>
                </MenuItem>

                <Divider />

                {isSuperAdmin && selectedUser?.role !== 'SUPER_ADMIN' && (
                    <MenuItem
                        onClick={() => {
                            setDeleteDialog({ open: true, user: selectedUser });
                            closeMenu();
                        }}
                        sx={{ color: '#f44336' }}
                    >
                        <ListItemIcon>
                            <PersonRemove fontSize="small" sx={{ color: '#f44336' }} />
                        </ListItemIcon>
                        <ListItemText>Delete User</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Create User/Admin Dialog */}
            <Dialog
                open={createDialog.open}
                onClose={() => setCreateDialog({ open: false, type: 'user' })}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                    color: 'white',
                    textAlign: 'center'
                }}>
                    <Typography variant="h5" fontWeight={700}>
                        {createDialog.type === 'admin' ? '🛡️ Create New Admin' : '👤 Create New User'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Display Name"
                            value={formData.displayName}
                            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Password"
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required
                        />

                        {createDialog.type === 'user' && (
                            <FormControl fullWidth>
                                <InputLabel>Role</InputLabel>
                                <Select
                                    value={formData.role}
                                    label="Role"
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                >
                                    <MenuItem value="USER">User</MenuItem>
                                    {isSuperAdmin && <MenuItem value="ADMIN">Admin</MenuItem>}
                                </Select>
                            </FormControl>
                        )}

                        <Stack direction="row" spacing={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                }
                                label="Active Account"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isVerified}
                                        onChange={(e) => setFormData({ ...formData, isVerified: e.target.checked })}
                                    />
                                }
                                label="Email Verified"
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button
                        onClick={() => setCreateDialog({ open: false, type: 'user' })}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateUser}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #2e7d32, #43a047)'
                            }
                        }}
                    >
                        Create {createDialog.type === 'admin' ? 'Admin' : 'User'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Role Dialog */}
            <Dialog
                open={editDialog.open}
                onClose={() => setEditDialog({ open: false, user: null, mode: 'edit' })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Change User Role</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={editDialog.user?.role || ''}
                            label="Role"
                            onChange={(e) => handleRoleChange(editDialog.user?.id, e.target.value)}
                        >
                            <MenuItem value="USER">User</MenuItem>
                            {isAdmin && <MenuItem value="ADMIN">Admin</MenuItem>}
                            {isSuperAdmin && <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog({ open: false, user: null, mode: 'edit' })}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete User Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, user: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: '#f44336' }}>⚠️ Delete User</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to permanently delete user <strong>"{deleteDialog.user?.displayName}"</strong>?
                        This action cannot be undone and will remove all associated data.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, user: null })}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteUser}
                        color="error"
                        variant="contained"
                        startIcon={<Delete />}
                    >
                        Delete Permanently
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog
                open={resetDialog.open}
                onClose={() => setResetDialog({ open: false, user: null, newPassword: '' })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>🔐 Reset Password</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="New Password"
                        type="password"
                        value={resetDialog.newPassword}
                        onChange={(e) => setResetDialog({ ...resetDialog, newPassword: e.target.value })}
                        sx={{ mt: 2 }}
                        helperText="Enter a strong password with at least 8 characters"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialog({ open: false, user: null, newPassword: '' })}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleResetPassword}
                        variant="contained"
                        disabled={resetDialog.newPassword.length < 8}
                    >
                        Reset Password
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Actions Dialog */}
            <Dialog
                open={bulkDialog.open}
                onClose={() => setBulkDialog({ open: false, action: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Bulk Actions ({selectedUsers.length} users selected)</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<CheckCircle />}
                            onClick={() => setBulkDialog({ ...bulkDialog, action: 'activate' })}
                            fullWidth
                        >
                            Activate Selected Users
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Block />}
                            onClick={() => setBulkDialog({ ...bulkDialog, action: 'deactivate' })}
                            fullWidth
                        >
                            Deactivate Selected Users
                        </Button>
                        {isSuperAdmin && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Delete />}
                                onClick={() => setBulkDialog({ ...bulkDialog, action: 'delete' })}
                                fullWidth
                            >
                                Delete Selected Users
                            </Button>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkDialog({ open: false, action: null })}>
                        Cancel
                    </Button>
                    {bulkDialog.action && (
                        <Button
                            onClick={handleBulkAction}
                            variant="contained"
                            color={bulkDialog.action === 'delete' ? 'error' : 'primary'}
                        >
                            Confirm {bulkDialog.action}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* User Details Dialog - Comprehensive Version */}
            <UserDetailsDialog
                open={userDetailsDialog.open}
                onClose={() => setUserDetailsDialog({ open: false, userId: null })}
                userId={userDetailsDialog.userId}
            />
        </Box>
    );
};

export default UserManagementTable;
